import React, { useRef, useState } from 'react';
import PageTitle from '../components/SEO/PageTitle';
import { useAuth } from '../contexts/AuthContext';
import { useCoverLetter } from '../hooks/useCoverLetter';
import CoverLetterForm from '../components/CoverLetter/CoverLetterForm';
import CoverLetterPreview from '../components/CoverLetter/CoverLetterPreview';
import TemplateExamples from '../components/CoverLetter/TemplateExamples';
import LoginModal from '../components/Auth/LoginModal';

const CoverLetter = () => {
    const { currentUser } = useAuth();
    const previewRef = useRef(null);
    const [showLoginModal, setShowLoginModal] = useState(false);
    
    const {
        coverData,
        loading,
        updatePersonal,
        updateRecipient,
        updateJobDetails,
        updateOpening,
        updateBody,
        updateClosing,
        updateAdditional,
        resetToDefaults,
        loadExample
    } = useCoverLetter();

    const handleDownloadPDF = async () => {
        if (!currentUser) {
            setShowLoginModal(true);
            return;
        }

        const element = document.getElementById('cover-letter-preview');
        if (!element) return;

        try {
            const html2pdf = (await import('html2pdf.js')).default;
            const opt = {
                margin: [0.5, 0.5, 0.5, 0.5],
                filename: `Cover_Letter_${coverData.personal.fullName.replace(/\s/g, '_')}.pdf`,
                image: { type: 'jpeg', quality: 0.98 },
                html2canvas: { scale: 2, letterRendering: true },
                jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' }
            };
            html2pdf().set(opt).from(element).save();
        } catch (error) {
            console.error('Error generating PDF:', error);
            alert('Error generating PDF. Please try again.');
        }
    };

    const handlers = {
        updatePersonal,
        updateRecipient,
        updateJobDetails,
        updateOpening,
        updateBody,
        updateClosing,
        updateAdditional
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-96">
                <i className="fas fa-spinner fa-spin text-4xl text-gradpath-green"></i>
                <span className="ml-3 text-gray-600">Loading...</span>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 py-8">
            <PageTitle 
    title="Cover Letter Builder" 
    description="Create a free professional cover letter online with our easy-to-use cover letter builder. Download as PDF for job applications."
/>
            <div className="text-center mb-8">
                <h1 className="text-3xl md:text-4xl font-bold text-gradpath-dark mb-2">Cover Letter Builder</h1>
                <p className="text-gray-600">Create a professional cover letter with our templates</p>
            </div>

            <div className="flex flex-col lg:flex-row gap-8">
                {/* Left Column - Form */}
                <div className="lg:w-1/2">
                    <TemplateExamples onLoadExample={loadExample} />
                    <CoverLetterForm coverData={coverData} handlers={handlers} />
                    
                    <div className="flex gap-4 mt-6">
                        <button
                            onClick={resetToDefaults}
                            className="flex-1 bg-gray-200 text-gray-700 font-semibold py-3 rounded-lg hover:bg-gray-300 transition-all flex items-center justify-center gap-2"
                        >
                            <i className="fas fa-undo-alt"></i> Reset to Defaults
                        </button>
                        <button
                            onClick={handleDownloadPDF}
                            className="flex-1 bg-gradient-to-r from-gradpath-green to-gradpath-dark text-white font-semibold py-3 rounded-lg hover:shadow-lg transition-all flex items-center justify-center gap-2"
                        >
                            <i className="fas fa-download"></i> Download PDF
                        </button>
                    </div>
                </div>

                {/* Right Column - Preview */}
                <div className="lg:w-1/2">
                    <div className="sticky top-24">
                        <div className="bg-gray-100 rounded-xl p-4">
                            <h3 className="font-bold text-gradpath-dark mb-3 flex items-center gap-2">
                                <i className="fas fa-eye text-gradpath-green"></i> Live Preview
                            </h3>
                            <div className="max-h-[80vh] overflow-y-auto">
                                <div id="cover-letter-preview">
                                    <CoverLetterPreview coverData={coverData} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Login Modal */}
            <LoginModal isOpen={showLoginModal} onClose={() => setShowLoginModal(false)} />
        </div>
    );
};

export default CoverLetter;